import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Product } from '@/types/product';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';

interface Props {
  title: string;
  subtitle: string;
  products: Product[];
}

const FeaturedProducts: React.FC<Props> = ({ title, subtitle, products }) => {
  const { tl } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const productsPerViewMobile = 2;
  const productsPerViewDesktop = 3;
  const totalProducts = products.length;

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalProducts);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalProducts) % totalProducts);
  };

  // Get products for mobile (2) and desktop (3)
  const visibleProductsMobile = products.slice(currentIndex, currentIndex + productsPerViewMobile);
  const visibleProductsDesktop = products.slice(currentIndex, currentIndex + productsPerViewDesktop);

  // Ensure we always have the right number of products in view
  const adjustedProductsMobile = visibleProductsMobile.length < productsPerViewMobile
    ? [...visibleProductsMobile, ...products.slice(0, productsPerViewMobile - visibleProductsMobile.length)]
    : visibleProductsMobile;

  const adjustedProductsDesktop = visibleProductsDesktop.length < productsPerViewDesktop
    ? [...visibleProductsDesktop, ...products.slice(0, productsPerViewDesktop - visibleProductsDesktop.length)]
    : visibleProductsDesktop;

  return (
    <section className="section bg-background">
      <div className="container-custom">
        <div className="mb-12 text-center">
          <h2 className="text-3xl font-semibold leading-[1.15] tracking-tight text-black md:text-4xl lg:text-[2.75rem]">
            {title}
          </h2>
          <div className="mt-4 flex flex-col items-center justify-center gap-4 md:flex-row">
            <p className="max-w-2xl text-sm leading-relaxed text-neutral-500 md:text-[15px]">
              {subtitle}
            </p>
            <Button asChild variant="luxury" size="sm" className="self-center rounded-none">
              <Link to="/products">
                {tl('Voir plus', 'View more', 'Vedi di più')} <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
        <div className="relative flex items-center">
          <button
            onClick={prevSlide}
            aria-label={tl('Précédent', 'Previous', 'Precedente')}
            className="absolute left-0 z-10 bg-black text-white rounded-full p-2 shadow-soft -translate-y-8 md:translate-y-0"
          >
            <ArrowLeft className="w-4 h-4 md:w-6 md:h-6 text-white" />
          </button>
          <div className="flex justify-center gap-2 md:gap-6 w-full transition-transform duration-300 ease-in-out">
            {/* Mobile: Show 2 products */}
            <div className="flex gap-2 md:hidden w-full justify-center">
              {adjustedProductsMobile.map((product, index) => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="flex-none group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 relative w-1/2"
                >
                  <div className="relative aspect-square w-full">
                    <img
                      src={product.images[0] || '/placeholder.svg'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-3 md:p-6 bg-white/30 md:bg-white/50 backdrop-blur-sm">
                      <h4 className="text-sm text-black md:text-lg font-semibold">{product.name}</h4>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            
            {/* Desktop: Show 3 products */}
            <div className="hidden md:flex gap-6 w-full justify-center">
              {adjustedProductsDesktop.map((product, index) => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="flex-none group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 relative w-1/3"
                >
                  <div className="relative aspect-square w-full">
                    <img
                      src={product.images[0] || '/placeholder.svg'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-3 md:p-6 bg-white/30 md:bg-white/50 backdrop-blur-sm">
                      <h4 className="text-sm text-black md:text-lg font-semibold">{product.name}</h4>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <button
            onClick={nextSlide}
            aria-label={tl('Suivant', 'Next', 'Successivo')}
            className="absolute right-0 z-10 bg-black text-white rounded-full p-2 shadow-soft -translate-y-8 md:translate-y-0"
          >
            <ArrowRight className="w-4 h-4 md:w-6 md:h-6 text-white" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
